import {
  digitsOnlyPhone,
  formatUSPhoneNumber,
  slugify,
} from "~/lib/helpers/storehelpers";
import { ProductCard } from "~/components/ProductCard";
import {
  Clock,
  Copy,
  Mail,
  MapPin,
  PersonStanding,
  Phone,
  Truck,
} from "lucide-react";
import { useStoreContext } from "~/hooks/use-store";
import { Card, CardContent } from "~/components/ui/card";
import StoreReviews from "~/components/admin/store/StoreReviews";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { useState } from "react";
import { Button } from "~/components/ui/button";
import { ZipCodeMap } from "~/components/ZipCodeMap";
import { usZip5Base } from "~/lib/usZip";
import { SectionHeader } from "./_index";
import HorizontalScroller from "~/components/HorizontalScroller";
import { StoreEmptyProducts } from "~/components/store/StoreEmptyProducts";

export default function StorePageHome() {
  const { storeDetails, storeReviews, storeAddress, featuredProducts } =
    useStoreContext();
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [copiedPhone, setCopiedPhone] = useState(false);

  const storePhoneDigits = digitsOnlyPhone(storeDetails.phone_number);
  const storePhoneTelHref = storePhoneDigits.length === 10
    ? `tel:+1${storePhoneDigits}`
    : storePhoneDigits
    ? `tel:${storePhoneDigits}`
    : "";
  const storePhoneDisplay = storeDetails.phone_number
    ? formatUSPhoneNumber(storeDetails.phone_number)
    : "";

  const opHours = storeDetails.operation_hours;
  console.log(opHours);
  const opHoursText = typeof opHours === "string"
    ? opHours.trim() || null
    : opHours != null
    ? JSON.stringify(opHours)
    : null;

  const recentReviews = [...storeReviews]
    .sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    )
    .slice(0, 3);

  /** Prefer mailing address ZIP; map used to depend only on Stores.zip (often unset). */
  const zipForMap =
    (storeAddress?.zip_code != null &&
      String(storeAddress.zip_code).trim() !== "" &&
      String(storeAddress.zip_code)) ||
    (storeDetails.zip != null &&
      String(storeDetails.zip).trim() !== "" &&
      String(storeDetails.zip)) ||
    "";

  const markerAddress =
    storeAddress &&
    [
      storeAddress.street,
      storeAddress.city,
      storeAddress.state,
      storeAddress.zip_code,
    ]
      .filter((p) => p != null && String(p).trim() !== "")
      .join(", ") || null;

  const copyToClipboard = async (text: string, type: "email" | "phone") => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === "email") {
        setCopiedEmail(true);
        setTimeout(() => setCopiedEmail(false), 2000);
      } else {
        setCopiedPhone(true);
        setTimeout(() => setCopiedPhone(false), 2000);
      }
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <TooltipProvider>
      <div className="flex flex-1 flex-col">
        <section className="flex-1 w-full py-12 md:py-24 lg:py-12">
          <div className="container px-4 md:px-6 max-w-7xl mx-auto">
            {/* Store name & summary */}
            <div className="flex flex-col items-center justify-center space-y-2 text-center mb-12">
              <h2 className="text-3xl font-bold tracking-tighter sm:text-5xl">
                {storeDetails.name}
              </h2>
              {storeDetails.summary && (
                <p className="max-w-[900px] text-gray-500 md:text-xl/relaxed lg:text-base/relaxed xl:text-xl/relaxed dark:text-gray-400">
                  {storeDetails.summary}
                </p>
              )}
            </div>

            {/* Featured Products Section */}
            <div className="mb-12">
              <SectionHeader
                title="Featured Products"
                seeAllHref={featuredProducts.length > 0
                  ? `/store/${slugify(storeDetails.name)}/products`
                  : undefined}
              />
              {featuredProducts.length > 0
                ? (
                  <HorizontalScroller>
                    {featuredProducts.map((product) => (
                      <div
                        className="flex w-[200px] shrink-0 self-stretch"
                        key={product.id}
                      >
                        <ProductCard
                          className="h-full min-h-0"
                          productId={product.id}
                          productDetails={product}
                          storeName={storeDetails.name}
                          size="promo"
                        />
                      </div>
                    ))}
                  </HorizontalScroller>
                )
                : (
                  <StoreEmptyProducts
                    storeName={storeDetails.name}
                    compact
                  />
                )}
            </div>

            {/* Recent Reviews Preview */}
            {recentReviews.length > 0 && (
              <div className="mb-12">
                <SectionHeader
                  title="Recent Reviews"
                  seeAllHref={`/store/${slugify(storeDetails.name)}/reviews`}
                />
                <div className="space-y-4">
                  {recentReviews.map((review) => (
                    <Card
                      key={review.id}
                      className="hover:shadow-md transition-shadow"
                    >
                      <CardContent className="pt-6">
                        <StoreReviews {...review} />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Contact Section */}
            {(storeDetails.email || storeDetails.phone_number) && (
              <div className="mb-12">
                <h3 className="text-2xl font-semibold mb-6">Contact</h3>
                <div className="space-y-4">
                  {storeDetails.email && (
                    <div className="flex items-center gap-2 md:gap-1">
                      <Mail className="h-5 w-5 text-gray-600 dark:text-gray-400 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300 flex-1">
                        {storeDetails.email}
                      </span>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() =>
                              copyToClipboard(
                                storeDetails.email ?? "",
                                "email",
                              )}
                          >
                            <Copy className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{copiedEmail ? "Copied!" : "Copy email"}</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  )}
                  {storeDetails.phone_number && (
                    <div className="flex items-center gap-2 md:gap-1">
                      <Phone className="h-5 w-5 text-gray-600 dark:text-gray-400 flex-shrink-0" />
                      <a
                        className="text-gray-700 dark:text-gray-300 flex-1 hover:underline"
                        href={storePhoneTelHref || undefined}
                      >
                        {storePhoneDisplay}
                      </a>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() =>
                              copyToClipboard(
                                storePhoneDigits ||
                                  String(storeDetails.phone_number ?? ""),
                                "phone",
                              )}
                          >
                            <Copy className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{copiedPhone ? "Copied!" : "Copy phone number"}</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  )}
                </div>
              </div>
            )}

            {opHoursText && (
              <div className="mb-12">
                <h3 className="text-2xl font-semibold mb-6">Opening hours</h3>
                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-gray-600 dark:text-gray-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 dark:text-gray-300 whitespace-pre-line">
                    {opHoursText}
                  </span>
                </div>
              </div>
            )}

            {/* Store Address Section */}
            {(storeAddress || zipForMap) && (
              <div className="mb-12">
                <h3 className="text-2xl font-semibold mb-6">Location</h3>
                <div className="flex flex-col gap-2 mb-4">
                  {storeDetails.offers_shipping &&
                    storeDetails.delivery_radius != null &&
                    storeDetails.delivery_radius > 0 && (
                    <div className="flex items-center gap-2">
                      <Truck className="h-5 w-5 text-gray-600 dark:text-gray-400 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Delivers within {storeDetails.delivery_radius} miles
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <PersonStanding className="h-5 w-5 text-gray-600 dark:text-gray-400 flex-shrink-0" />
                    <span className="text-gray-700 dark:text-gray-300">
                      Available for pickup
                    </span>
                  </div>
                </div>
                {storeAddress && (
                  <Card className="mb-6 max-w-md">
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-3">
                        <MapPin className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                        <div className="text-gray-700 dark:text-gray-300">
                          {storeAddress.street && (
                            <p className="font-medium">{storeAddress.street}</p>
                          )}
                          <p>
                            {[
                              storeAddress.city,
                              storeAddress.state,
                              storeAddress.zip_code,
                            ].filter(Boolean).join(", ")}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
                {zipForMap && usZip5Base(zipForMap) && (
                  <ZipCodeMap
                    zipCode={zipForMap}
                    markerAddress={markerAddress}
                    deliveryRadius={storeDetails.delivery_radius ?? null}
                  />
                )}
              </div>
            )}
          </div>
        </section>
      </div>
    </TooltipProvider>
  );
}
